import { useState } from "react";
import { ChevronDown, ChevronRight, ListChecks, Loader2, RefreshCw } from "lucide-react";
import api, { apiErr, fmtCredits } from "../lib/api";

const date = value => value ? new Date(value).toLocaleString("es-ES", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
}) : "Fecha no registrada";
const deltaText = row => typeof row.delta === "number"
  ? `${row.delta > 0 ? "+" : row.delta < 0 ? "−" : ""}${fmtCredits(Math.abs(row.delta))}`
  : row.unknown_delta ? "No registrado" : "—";

export default function AdminUserActivity({ uid, currentBalance }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = async (page = 1) => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/admin/users/${uid}/activity`, { params: { page, limit: 50 } });
      setResult(previous => ({ ...data, items: page === 1 ? data.items
        : [...(previous?.items || []), ...data.items.filter(row => !previous?.items.some(old => old.id === row.id))] }));
    } catch (e) { setError(apiErr(e)); }
    finally { setBusy(false); }
  };
  const toggle = () => { setOpen(!open); if (!open && !result) load(); };
  return <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <button onClick={toggle} data-testid="admin-activity-toggle" className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50">
      <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}<ListChecks size={16} /> Actividad
      </span>
      <span className="text-sm font-semibold text-blue-700" data-testid="activity-current-balance">
        Saldo actual: {fmtCredits(result?.balance_current ?? currentBalance)} créditos
      </span>
    </button>
    {open && <div className="border-t border-slate-100">
      <div className="flex items-center justify-between gap-2 px-4 py-2 text-xs text-slate-500">
        <span>Consumos, recargas, premios, compensaciones y renovaciones</span>
        <button onClick={() => load()} disabled={busy} className="flex items-center gap-1 text-blue-600 disabled:opacity-50"><RefreshCw size={13} /> Actualizar</button>
      </div>
      {error && <p role="alert" className="px-4 py-2 text-sm text-rose-600">{error}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr>
            <th className="px-4 py-2 text-left">Fecha</th><th className="px-4 py-2 text-left">Acción</th>
            <th className="px-4 py-2 text-right">Saldo tras la acción</th>
          </tr></thead>
          <tbody>{(result?.items || []).map(row => <tr key={row.id} className="border-t border-slate-100">
            <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">{date(row.at)}</td>
            <td className="px-4 py-2 text-slate-700">
              <div>{row.label}</div>
              {row.reason && <div className="text-xs text-slate-500 break-words">{row.reason}</div>}
              {typeof row.amount === "number" && <div className="text-xs text-slate-500">Pago: {row.amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</div>}
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-right">
              <div className="font-semibold text-slate-700" data-testid="activity-balance-after">
                {typeof row.balance_after === "number" ? `${fmtCredits(row.balance_after)} créditos` : "No registrado"}
              </div>
              {(typeof row.delta === "number" || row.unknown_delta) && <div className={`text-xs ${row.delta > 0 ? "text-emerald-600" : row.delta < 0 ? "text-rose-600" : "text-slate-400"}`} data-testid="activity-delta">
                {deltaText(row)}{typeof row.delta === "number" ? " créditos" : ""}
              </div>}
            </td>
          </tr>)}</tbody>
        </table>
      </div>
      {busy && <div role="status" className="flex items-center justify-center gap-2 p-4 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Cargando actividad…</div>}
      {!busy && result?.items.length === 0 && <p className="p-4 text-sm text-slate-400">Sin actividad registrada.</p>}
      {result && result.items.length < result.total && <button disabled={busy} onClick={() => load(result.page + 1)} className="w-full border-t border-slate-100 py-3 text-sm font-medium text-blue-600 disabled:opacity-50">Cargar más ({result.items.length} de {result.total})</button>}
    </div>}
  </div>;
}
